import { createContext, useState, useCallback, useEffect, useMemo, useRef, ReactNode } from 'react';
import { timeTrackingService, TimeEntry } from '../services/timeTracking';

interface LoadProgress {
  loaded: number;
  estimated: number;
}

interface EntryFilter {
  projectId?: string;
  startDate?: Date;
  endDate?: Date;
  hasEndTime?: boolean;
  searchTerm?: string;
}

export interface TimeEntriesCacheContextType {
  // Data
  allEntries: TimeEntry[];
  loading: boolean;
  error: string | null;
  lastLoadedAt: Date | null;
  loadProgress: LoadProgress;

  // Operations
  loadAllEntries: () => Promise<void>;
  refreshCache: () => Promise<void>;
  invalidateCache: () => void;

  // Optimistic updates
  addEntryToCache: (entry: TimeEntry) => void;
  updateEntryInCache: (id: string, updates: Partial<TimeEntry>) => void;
  removeEntryFromCache: (id: string) => void;
  removeEntriesByProjectId: (projectId: string) => void;
  loadEntriesForProject: (projectId: string) => Promise<void>;

  // Queries (client-side)
  getEntries: (filter?: EntryFilter) => TimeEntry[];
  getPaginatedEntries: (page: number, pageSize: number, filter?: EntryFilter) => {
    entries: TimeEntry[];
    totalCount: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };

  // Cache status
  isStale: boolean;
  cacheSize: number;
}

// eslint-disable-next-line react-refresh/only-export-components
export const TimeEntriesCacheContext = createContext<TimeEntriesCacheContextType | null>(null);

interface TimeEntriesCacheProviderProps {
  children: ReactNode;
}

export function TimeEntriesCacheProvider({ children }: TimeEntriesCacheProviderProps) {
  const [allEntries, setAllEntries] = useState<TimeEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastLoadedAt, setLastLoadedAt] = useState<Date | null>(null);
  const [loadProgress, setLoadProgress] = useState<LoadProgress>({ loaded: 0, estimated: 0 });

  // Ref to prevent concurrent loads (survives re-renders, not affected by closures)
  const isLoadingRef = useRef(false);

  /**
   * Load ALL entries using token-based sequential pagination
   * This efficiently loads all data by making multiple requests with continuation tokens
   */
  const loadAllEntries = useCallback(async () => {
    // Safety check: Don't load if no auth token (silent skip - this is expected on initial load)
    const token = localStorage.getItem('authToken');
    if (!token) {
      return;
    }

    // Prevent concurrent loads (ref check survives component re-renders)
    if (isLoadingRef.current) {
      return;
    }

    try {
      isLoadingRef.current = true;
      setLoading(true);
      setError(null);
      setLoadProgress({ loaded: 0, estimated: 0 });

      let allFetchedEntries: TimeEntry[] = [];
      let nextToken: string | undefined = undefined;
      let totalCount = 0;
      const BATCH_SIZE = 1000;


      // Sequential token-based loading
      do {
        const response = await timeTrackingService.getTimeEntriesWithToken(
          BATCH_SIZE,
          nextToken,
          undefined,
          true // activeProjectsOnly: exclude archived project entries from cache
        );

        allFetchedEntries = [...allFetchedEntries, ...response.timeEntries];
        nextToken = response.nextToken;
        totalCount = response.totalCount;

        // Update progress
        setLoadProgress({
          loaded: allFetchedEntries.length,
          estimated: totalCount
        });

      } while (nextToken);

      setAllEntries(allFetchedEntries);
      setLastLoadedAt(new Date());


    } catch (err) {
      console.error('❌ Error loading all entries:', err);
      setError(err instanceof Error ? err.message : 'Failed to load entries');
    } finally {
      isLoadingRef.current = false;
      setLoading(false);
    }
  }, []);

  /**
   * Refresh the cache by reloading all entries
   */
  const refreshCache = useCallback(async () => {
    console.log('🔄 Refreshing cache...');
    await loadAllEntries();
  }, [loadAllEntries]);

  /**
   * Clear the cache
   */
  const invalidateCache = useCallback(() => {
    console.log('🗑️ Invalidating cache...');
    setAllEntries([]);
    setLastLoadedAt(null);
    setLoadProgress({ loaded: 0, estimated: 0 });
  }, []);

  /**
   * Optimistic add - immediately add entry to cache
   */
  const addEntryToCache = useCallback((entry: TimeEntry) => {
    console.log('➕ Adding entry to cache:', entry.id);
    setAllEntries(prev => [entry, ...prev]);
  }, []);

  /**
   * Optimistic update - immediately update entry in cache
   */
  const updateEntryInCache = useCallback((id: string, updates: Partial<TimeEntry>) => {
    console.log('✏️ Updating entry in cache:', id);
    setAllEntries(prev =>
      prev.map(entry => entry.id === id ? { ...entry, ...updates } : entry)
    );
  }, []);

  /**
   * Optimistic remove - immediately remove entry from cache
   */
  const removeEntryFromCache = useCallback((id: string) => {
    console.log('➖ Removing entry from cache:', id);
    setAllEntries(prev => prev.filter(entry => entry.id !== id));
  }, []);

  /**
   * Remove all entries belonging to a project (called when a project is archived)
   */
  const removeEntriesByProjectId = useCallback((projectId: string) => {
    setAllEntries(prev => prev.filter(entry => entry.projectId !== projectId));
  }, []);

  /**
   * Load entries for a specific project and merge into cache (called when a project is reactivated)
   */
  const loadEntriesForProject = useCallback(async (projectId: string) => {
    try {
      const response = await timeTrackingService.getEntriesByProjectId(projectId);
      setAllEntries(prev => {
        // Safety: only keep entries that actually belong to this project.
        // Prevents doubles if the server response unexpectedly includes entries
        // from other projects (e.g. if the projectId filter is bypassed).
        const projectEntries = response.timeEntries.filter(e => e.projectId === projectId);
        const withoutProject = prev.filter(e => e.projectId !== projectId);
        return [...projectEntries, ...withoutProject];
      });
    } catch (err) {
      console.error('Error loading entries for project:', projectId, err);
    }
  }, []);

  /**
   * Get entries with client-side filtering
   */
  const getEntries = useCallback((filter?: EntryFilter): TimeEntry[] => {
    let filtered = [...allEntries];

    if (filter) {
      if (filter.projectId) {
        filtered = filtered.filter(e => e.projectId === filter.projectId);
      }

      if (filter.startDate) {
        filtered = filtered.filter(e => new Date(e.startTime) >= filter.startDate!);
      }

      if (filter.endDate) {
        filtered = filtered.filter(e => new Date(e.startTime) <= filter.endDate!);
      }

      if (filter.hasEndTime !== undefined) {
        filtered = filtered.filter(e => (e.endTime !== null && e.endTime !== undefined) === filter.hasEndTime);
      }

      if (filter.searchTerm) {
        const term = filter.searchTerm.toLowerCase();
        filtered = filtered.filter(e =>
          e.description.toLowerCase().includes(term) ||
          e.location?.toLowerCase().includes(term)
        );
      }
    }

    return filtered;
  }, [allEntries]);

  /**
   * Get paginated entries with client-side pagination (instant!)
   */
  const getPaginatedEntries = useCallback((
    page: number,
    pageSize: number,
    filter?: EntryFilter
  ) => {
    const filtered = getEntries(filter);
    const totalCount = filtered.length;
    const totalPages = Math.ceil(totalCount / pageSize);
    const start = (page - 1) * pageSize;
    const entries = filtered.slice(start, start + pageSize);

    return {
      entries,
      totalCount,
      totalPages,
      hasNextPage: page < totalPages,
      hasPrevPage: page > 1
    };
  }, [getEntries]);

  /**
   * Check if cache is stale (>10 minutes old)
   */
  const isStale = useMemo(() => {
    if (!lastLoadedAt) return true;
    const tenMinutesAgo = new Date();
    tenMinutesAgo.setMinutes(tenMinutesAgo.getMinutes() - 10);
    return lastLoadedAt < tenMinutesAgo;
  }, [lastLoadedAt]);

  const cacheSize = allEntries.length;

  /**
   * Auto-refresh on window focus if stale
   */
  useEffect(() => {
    const handleFocus = () => {
      const token = localStorage.getItem('authToken');
      if (!token) {
        return;
      }

      if (isStale && !loading && allEntries.length > 0) {
        console.log('🔄 Auto-refreshing stale cache on window focus');
        refreshCache();
      }
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [isStale, loading, allEntries.length, refreshCache]);

  /**
   * Initial load on mount
   */
  useEffect(() => {
    if (allEntries.length === 0 && !loading) {
      loadAllEntries();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  const value: TimeEntriesCacheContextType = {
    allEntries,
    loading,
    error,
    lastLoadedAt,
    loadProgress,
    loadAllEntries,
    refreshCache,
    invalidateCache,
    addEntryToCache,
    updateEntryInCache,
    removeEntryFromCache,
    removeEntriesByProjectId,
    loadEntriesForProject,
    getEntries,
    getPaginatedEntries,
    isStale,
    cacheSize,
  };

  return (
    <TimeEntriesCacheContext.Provider value={value}>
      {children}
    </TimeEntriesCacheContext.Provider>
  );
}
